import { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Check } from 'lucide-react';
import { useBrand } from '@/shared/lib/brand';
import { currency, decimal , type CurrencyCode} from '@/shared/lib/format';
import { cn } from '@/shared/lib/cn';

export type OperatorSuccessReward = {
  program: 'cashback' | 'points' | 'stamps' | 'balance' | 'giftcard';
  awardUnits: number;
  cashbackEarned: number;
  pointsEarned: number;
  stampsEarned: number;
  quantityLabel: string;
  /** Produto do catálogo resgatado. */
  redemptionProductName?: string;
  redemptionProductCost?: number;
};

interface OperatorSuccessOverlayProps {
  open: boolean;
  reward: OperatorSuccessReward | null;
  currencyCode: CurrencyCode;
  onComplete: () => void;
}

function rewardText(reward: OperatorSuccessReward, currencyCode: CurrencyCode): string | null {
  if (reward.awardUnits <= 0) return null;
  if (reward.program === 'cashback') {
    return currency(reward.cashbackEarned, currencyCode).replace(/\s/g, '');
  }
  if (reward.program === 'points') {
    return `${decimal(reward.pointsEarned || reward.awardUnits)} pts`;
  }
  if (reward.program === 'stamps') {
    const n = reward.stampsEarned || reward.awardUnits;
    return `${decimal(n)} ${reward.quantityLabel.toLowerCase()}`;
  }
  return null;
}

function redemptionText(reward: OperatorSuccessReward): string | null {
  if (reward.redemptionProductName) {
    return `Resgatou: ${reward.redemptionProductName}`;
  }
  return null;
}

function fireConfetti(): () => void {
  const colors = ['#ffc001', '#404040', '#ffffff', '#22c55e', '#fbbf24'];
  void confetti({
    particleCount: 90,
    spread: 70,
    origin: { y: 0.55 },
    colors,
    disableForReducedMotion: true,
  });
  const burst = window.setTimeout(() => {
    void confetti({
      particleCount: 50,
      angle: 60,
      spread: 55,
      origin: { x: 0.15, y: 0.65 },
      colors,
      disableForReducedMotion: true,
    });
    void confetti({
      particleCount: 50,
      angle: 120,
      spread: 55,
      origin: { x: 0.85, y: 0.65 },
      colors,
      disableForReducedMotion: true,
    });
  }, 180);
  return () => window.clearTimeout(burst);
}

/**
 * Overlay pós-confirmação: morfismo do ícone Fixe → check,
 * e se houver ganho, valor + confete antes de voltar à identificação.
 */
export function OperatorSuccessOverlay({
  open,
  reward,
  currencyCode,
  onComplete,
}: OperatorSuccessOverlayProps) {
  const { brand } = useBrand();
  const reduceMotion = useReducedMotion();
  const [phase, setPhase] = useState<'logo' | 'check' | 'reward'>('logo');
  const gained = reward ? rewardText(reward, currencyCode) : null;
  const redeemed = reward ? redemptionText(reward) : null;
  const showRewardPhase = Boolean(gained || redeemed);

  useEffect(() => {
    if (!open) {
      setPhase('logo');
      return;
    }

    let clearConfetti: (() => void) | undefined;
    const tCheck = window.setTimeout(() => setPhase('check'), reduceMotion ? 80 : 520);
    const tReward = window.setTimeout(() => {
      if (showRewardPhase) {
        setPhase('reward');
        if (!reduceMotion) clearConfetti = fireConfetti();
      }
    }, reduceMotion ? 280 : 980);
    const tDone = window.setTimeout(
      () => onComplete(),
      showRewardPhase ? (reduceMotion ? 900 : 2600) : reduceMotion ? 500 : 1400,
    );

    return () => {
      window.clearTimeout(tCheck);
      window.clearTimeout(tReward);
      window.clearTimeout(tDone);
      clearConfetti?.();
    };
  }, [open, showRewardPhase, onComplete, reduceMotion]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-background/70 px-4 backdrop-blur-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.28 }}
          role="status"
          aria-live="polite"
          aria-label="Conta confirmada"
        >
          <motion.div
            className="flex w-full max-w-sm flex-col items-center text-center"
            initial={{ opacity: 0, scale: 0.92, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
          >
            <div className="relative grid size-28 place-items-center sm:size-32">
              <AnimatePresence mode="wait">
                {phase === 'logo' ? (
                  <motion.div
                    key="logo"
                    className="absolute inset-0 grid place-items-center"
                    initial={{ opacity: 0, scale: 0.7, rotate: -8 }}
                    animate={{ opacity: 1, scale: 1, rotate: 0 }}
                    exit={{ opacity: 0, scale: 0.55, rotate: 12, filter: 'blur(4px)' }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <img
                      src={brand.assets.icon.brand}
                      alt=""
                      width={88}
                      height={88}
                      decoding="async"
                      className="size-20 object-contain drop-shadow-[0_8px_24px_rgba(255,192,1,0.4)] sm:size-[5.5rem] dark:drop-shadow-[0_0_28px_rgba(255,192,1,0.5)]"
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="check"
                    className="absolute inset-0 grid place-items-center"
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ type: 'spring', stiffness: 380, damping: 22 }}
                  >
                    <span
                      className={cn(
                        'grid size-20 place-items-center rounded-full bg-primary text-primary-foreground shadow-glass sm:size-[5.5rem]',
                        'ring-4 ring-primary/25',
                      )}
                    >
                      <Check className="size-10 sm:size-12" strokeWidth={2.75} />
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <motion.p
              className="mt-6 text-lg font-semibold tracking-tight sm:text-xl"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              Conta confirmada
            </motion.p>

            <AnimatePresence>
              {phase === 'reward' && showRewardPhase ? (
                <motion.div
                  key="reward"
                  className="mt-4 space-y-3"
                  initial={{ opacity: 0, y: 16, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 24 }}
                >
                  {gained ? (
                    <div>
                      <p className="text-sm text-muted-foreground">Cliente ganhou</p>
                      <p className="mt-1 text-3xl font-bold tabular-nums tracking-tight text-primary sm:text-4xl">
                        {gained}
                      </p>
                    </div>
                  ) : null}
                  {redeemed ? (
                    <div>
                      <p className="text-sm text-muted-foreground">{redeemed}</p>
                    </div>
                  ) : null}
                </motion.div>
              ) : null}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
